import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { FileText, Search, Filter, Download } from "lucide-react";
import Header from "@/components/Header";
import api, { uploadFileUrl } from "@/lib/api";
import { usePeriod } from "@/contexts/PeriodContext";
import { fmtShekel, MONTHS_HE } from "@/lib/utils";

const SOURCE_STYLES = {
  fixed_expense: { bg: "#FEF3C7", text: "#92400E", label: "קבועה" },
  variable_expense: { bg: "#E0F2FE", text: "#075985", label: "משתנה" },
  income: { bg: "#DCFCE7", text: "#166534", label: "הכנסה" },
  business: { bg: "#F3E8FF", text: "#6B21A8", label: "עסק" },
};

export default function FilesPage() {
  const { month, year, view, setMonth, setYear, setView } = usePeriod();
  const [files, setFiles] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | fixed_expense | ...

  useEffect(() => {
    const load = async () => {
      const { data } = await api.get("/files");
      setFiles(data);
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    return files.filter((f) => {
      if (filter !== "all" && f.source_type !== filter) return false;
      if (query) {
        const q = query.toLowerCase();
        return (
          (f.label || "").toLowerCase().includes(q) ||
          (f.filename || "").toLowerCase().includes(q) ||
          (f.source_type_label || "").toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [files, query, filter]);

  const counts = useMemo(() => {
    return {
      all: files.length,
      fixed_expense: files.filter((f) => f.source_type === "fixed_expense").length,
      variable_expense: files.filter((f) => f.source_type === "variable_expense").length,
      income: files.filter((f) => f.source_type === "income").length,
      business: files.filter((f) => f.source_type === "business").length,
    };
  }, [files]);

  return (
    <div className="min-h-screen">
      <Header
        month={month}
        year={year}
        view={view}
        setMonth={setMonth}
        setYear={setYear}
        setView={setView}
        showFilters={false}
        title="כל הקבצים"
        backTo="/app"
      />
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8 space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-surface p-6 md:p-8"
        >
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center"
                style={{ background: "#E0F2FE", color: "#075985" }}
              >
                <FileText size={22} />
              </div>
              <div>
                <div className="font-heebo font-bold text-xl text-slate-900">
                  ארכיון קבצים
                </div>
                <div className="text-xs text-slate-500">
                  {files.length} קבצים · חשבוניות, קבלות ומסמכים
                </div>
              </div>
            </div>
            <div className="relative">
              <Search
                size={16}
                className="absolute top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ insetInlineStart: "0.75rem" }}
                color="#9CA3AF"
              />
              <input
                className="input-field ps-9"
                placeholder="חפש לפי שם / לקוח / סוג…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="files-search"
                style={{ paddingInlineStart: "2.25rem", minWidth: 260 }}
              />
            </div>
          </div>

          {/* Filter chips */}
          <div className="flex flex-wrap gap-2 mb-5">
            {[
              { k: "all", label: "הכל" },
              { k: "fixed_expense", label: "הוצאות קבועות" },
              { k: "variable_expense", label: "הוצאות משתנות" },
              { k: "income", label: "הכנסות" },
              { k: "business", label: "עסק" },
            ].map((t) => {
              const active = filter === t.k;
              const style = SOURCE_STYLES[t.k] || { bg: "#F1F5F9", text: "#334155" };
              return (
                <button
                  key={t.k}
                  onClick={() => setFilter(t.k)}
                  className="chip"
                  style={{
                    background: active ? style.bg : "#F1F5F9",
                    color: active ? style.text : "#64748B",
                    boxShadow: active ? `0 0 0 2px ${style.text}33` : "none",
                  }}
                  data-testid={`files-filter-${t.k}`}
                >
                  {t.label} · {counts[t.k] ?? 0}
                </button>
              );
            })}
          </div>

          {filtered.length === 0 ? (
            <div className="empty-state">
              {files.length === 0
                ? "עדיין לא הועלו קבצים. צרף חשבוניות/קבלות בהוצאות ובהכנסות."
                : "אין תוצאות לחיפוש."}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((f, idx) => (
                <FileCard key={`${f.source_type}-${f.source_id}-${idx}`} file={f} />
              ))}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}

function FileCard({ file }) {
  const style = SOURCE_STYLES[file.source_type] || { bg: "#F1F5F9", text: "#334155", label: file.source_type_label };
  const ext = (file.filename || "").split(".").pop()?.toUpperCase() || "";
  const isImage = ["JPG", "JPEG", "PNG", "WEBP", "HEIC"].includes(ext);

  return (
    <a
      href={uploadFileUrl(file.filename)}
      target="_blank"
      rel="noreferrer"
      className="rounded-2xl border-2 p-4 hover:shadow-lg transition bg-white group"
      style={{ borderColor: style.text + "33" }}
      data-testid={`file-card-${file.filename}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-12 h-14 rounded-xl flex items-center justify-center relative shrink-0"
          style={{ background: style.bg, color: style.text }}
        >
          {isImage ? (
            <img
              src={uploadFileUrl(file.filename)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover rounded-xl"
            />
          ) : (
            <FileText size={22} />
          )}
          <span
            className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-white shadow border"
            style={{ color: style.text, borderColor: style.text + "55" }}
          >
            {ext}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="chip text-[10px] px-2 py-0.5"
              style={{ background: style.bg, color: style.text }}
            >
              {style.label}
            </span>
            {file.date && (
              <span className="text-xs text-slate-500">{file.date}</span>
            )}
          </div>
          <div
            className="font-heebo font-semibold text-slate-800 truncate"
            title={file.label}
          >
            {file.label || "ללא שם"}
          </div>
          {file.amount != null && (
            <div className="num-pop text-sm mt-1" style={{ color: style.text }}>
              {fmtShekel(file.amount)}
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
        <span>{file.month ? `${MONTHS_HE[file.month - 1]} ${file.year}` : ""}</span>
        <span className="text-emerald-700 font-heebo font-medium opacity-0 group-hover:opacity-100 transition">
          פתח קובץ ←
        </span>
      </div>
    </a>
  );
}
